import { lazy } from 'react';

import type {
  IntegrationDefinition,
  IntegrationLoaderContext,
  NormalizedConnection,
} from '@mint-lib/integration-registry';
import type { QueryClient } from '@mint-lib/query-client';
import { PlatformUrns } from '@mint-sdk/api-integrations';
import { Integrations } from '@mint-sdk/api-omni-trafficking';

import { cmIntegrationMeta } from './meta.js';
import { cmOAuthFlow } from './oauth.js';
import {
  cmConnectionRepository,
  toNormalizedCMConnection,
} from './repository.js';

// The literal constant, not `cmIntegrationMeta.urn` — see the same note in
// `oauth.ts`/`repository.ts`; the SDK's query schema expects the narrower
// `AdserverUrnEnum` literal union.
const CM_URN = PlatformUrns.TRAFFICKING_PLATFORM_URN.campaignManager;

/**
 * Loader-safe prefetch: repository methods are hooks and can't be called from
 * a router `loader`/`beforeLoad`, so this is the one place that can hand the
 * fetched list back to the router (e.g. to redirect read-only users to their
 * first existing connection) without duplicating `useList`'s query.
 */
async function prefetch(
  ctx: IntegrationLoaderContext,
): Promise<{ connections?: NormalizedConnection[] }> {
  const queryClient = ctx.queryClient as QueryClient;
  const items = await queryClient.ensureQueryData(
    Integrations.getIntegrationList({
      params: { adServer: CM_URN },
    }),
  );
  return { connections: (items ?? []).map(toNormalizedCMConnection) };
}

/**
 * Root factory for the Campaign Manager 360 integration. `AddForm`/`ManageForm`
 * are code-split via `React.lazy` — this is the only module in the package
 * that imports the `add-form`/`manage-form` subpaths.
 */
export function createCMIntegration(): IntegrationDefinition {
  return {
    meta: cmIntegrationMeta,
    connection: {
      AddForm: lazy(() => import('@mint-lib/cm-integration/add-form')),
      ManageForm: lazy(() => import('@mint-lib/cm-integration/manage-form')),
      repository: cmConnectionRepository,
      oauth: cmOAuthFlow,
      prefetch,
    },
  };
}
